// React imports
import React, { ChangeEvent, useEffect, useState } from 'react';

// Router imports
import { useNavigate } from 'react-router-dom';

// Redux imports
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../redux/store';
import { updateUserDetails } from '../redux/userSlice';

// API imports
import { getUserDetails, updateProfile } from '../api/UserService';

// AWS imports
import { Amplify } from 'aws-amplify';
import { awsConfig } from '../aws-exports';
import { fetchAuthSession, updateUserAttributes } from 'aws-amplify/auth';
import { uploadData } from 'aws-amplify/storage';

// Style imports
import '../styles/Idea.css';

// Asset imports
import NoPhoto from '../assets/no-photo.png';

// Component imports
import Label from './common/Label';
import Button from './common/Button';
import Input from './common/Input';
import CustomSelect from './common/Select';
import Grants from './common/Grants';
import Loading from './common/Loading';
import ImageCropper from './ImageCropper';
import Text from './common/Text';

// Type imports
import { UserDetailsRes, OptionType, IUserDetails } from '../types/types';

// Import types from react-select
import { MultiValue, SingleValue } from 'react-select';

// Constant imports
import { FACULTY } from '../constants/Constants';

/*
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
*/

const UpdateProfile: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const reduxUser = useSelector((state: RootState) => state.user.userDetails);
  const reduxUserTypes = useSelector((state: RootState) => state.userTypes);
  const reduxDepartments = useSelector((state: RootState) => state.departments);
  const reduxSchools = useSelector((state: RootState) => state.schools);

  const [userData, setUserData] = useState<UserDetailsRes>({
    tenant_id: 0,
    first_name: '',
    last_name: '',
    google_scholar_url: '',
    linkedin_url: '',
    headline: '',
    bio: '',
    location: '',
    profile_image_url: '',
    school_id: 0,
    department_id: 0,
    id: 0,
    selected_department_id: undefined,
    selected_school_id: undefined,
    selected_user_type: undefined,
    email: '',
    user_type_id: 0,
    grants: [],
  });

  const [loading, setLoading] = useState(false);
  const [onSubmitLoader, setOnSubmitLoader] = useState(false);
  const [isSavedProfile, setIsSavedProfile] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>('');
  const [userTypes, setUserTypes] = useState<OptionType[]>([]);
  const [departments, setDepartments] = useState<OptionType[]>([]);
  const [schools, setSchools] = useState<OptionType[]>([]);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string | undefined;
  }>({});
  const [bioCharCounts, setBioCharCounts] = useState(0);
  const [selectedFile, setSelectedFile] = useState<Blob | null>(null);
  const [tempImageURL, setTempImageURL] = useState<string | null>(null);

  const bioMaxChars = 1000;

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!reduxUser?.userId) return;
      setLoading(true);
      setErrorMessage(null);

      try {
        const mapToOptions = <T extends { id: number; name?: string }>(
          data: T[],
          labelKey: keyof T,
        ): OptionType[] => {
          return data.map((item) => ({
            label: item[labelKey] as string,
            value: item.id,
          }));
        };
        const userTypeOptions = mapToOptions(reduxUserTypes.userTypes, 'type');
        const departmentsOptions = mapToOptions(
          reduxDepartments.departments,
          'name',
        );
        const schoolsOptions = mapToOptions(reduxSchools.schools, 'name');
        setUserTypes(userTypeOptions);
        setDepartments(departmentsOptions);
        setSchools(schoolsOptions);
        const result = await getUserDetails(reduxUser?.userId);
        if ('code' in result) {
          if (result.code === 'FORBIDDEN' || result.code === 'UNAUTHORIZED') {
            setErrorMessage(result.message);
          } else {
            setErrorMessage(result.message);
          }
        } else {
          const resUserInfo = result as UserDetailsRes;
          setUserData({
            ...resUserInfo,
            selected_department_id: departmentsOptions.find(
              (option) => option.value === resUserInfo.department_id,
            ),
            selected_school_id: schoolsOptions.find(
              (option) => option.value === resUserInfo.school_id,
            ),
            selected_user_type: userTypeOptions.find(
              (option) => option.value === resUserInfo.user_type_id,
            ),
          });
          setBioCharCounts(
            resUserInfo.bio && resUserInfo.bio !== null
              ? resUserInfo.bio.length
              : 0,
          );
          setTempImageURL(resUserInfo.profile_image_url);
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        setErrorMessage('Failed to fetch user details.');
      } finally {
        setLoading(false);
      }
    };

    if (reduxUser?.userId) {
      fetchUserDetails();
    }
  }, [reduxUser?.userId]);

  const requiredFieldsWithLabels: {
    field: keyof UserDetailsRes;
    label: string;
  }[] = [
    { field: 'first_name', label: 'First name' },
    { field: 'last_name', label: 'Last name' },
    { field: 'user_type_id', label: 'User type' },
    { field: 'bio', label: 'Bio' },
    { field: 'linkedin_url', label: 'LinkedIn URL' },
  ];

  const validateRequiredFields = () => {
    const errors: { [key: string]: string } = {};

    // Define a regex for URL validation
    const urlPattern =
      /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

    requiredFieldsWithLabels.forEach(({ field, label }) => {
      const value = userData[field]?.toString().trim();

      // Check if the field is empty
      if (!value) {
        errors[field] = `${label} is required`;
      }

      // If the field is specifically a URL field, validate the format
      if (
        label.toLowerCase().includes('url') &&
        value &&
        !urlPattern.test(value)
      ) {
        errors[field] = `${label} must be a valid URL`;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const updateFormFields = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    setValidationErrors({});
    // Split the name by dots to handle nested properties like 'most_recent_experience.title'
    const keys = name.split('.');

    // Update state immutably for nested properties
    setUserData((prev) => {
      // Create a copy of the current state
      const newUserData = { ...prev };
      let nestedProperty: any = newUserData;

      // Iterate through keys to update the correct nested property
      keys.forEach((key, index) => {
        if (index === keys.length - 1) {
          // If this is the last key, set the value
          nestedProperty[key] =
            name === 'bio' ? value.slice(0, bioMaxChars) : value;
        } else {
          // Otherwise, navigate deeper into the object
          if (!(key in nestedProperty) || nestedProperty[key] === null) {
            // Initialize the nested object if it does not exist or is null
            nestedProperty[key] = {};
          }
          nestedProperty = nestedProperty[key];
        }
      });
      return newUserData;
    });

    // Update bio character count if 'bio' field is modified
    if (name === 'bio') {
      setBioCharCounts(Math.min(value.length, bioMaxChars));
    }
  };

  const handleOptionChange = (
    field: keyof UserDetailsRes,
    dropdownField: keyof UserDetailsRes,
    option: SingleValue<OptionType> | MultiValue<OptionType> | null,
  ) => {
    if (!option) return;
    const singleValue = option as SingleValue<OptionType>;
    setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    setUserData((prev) => ({
      ...prev,
      [dropdownField]: singleValue,
      [field]: singleValue?.value,
    }));
  };

  const initializeIdentityPool = async () => {
    awsConfig.aws_cognito_identity_pool_id =
      process.env.REACT_APP_AWS_COGNITO_IDENTITY_POOL_ID!;
    Amplify.configure(awsConfig);
    await fetchAuthSession();
  };

  const handleUploadComplete = (filePath: Blob, file: File) => {
    const getFileExtension = (mimeType: string) => {
      const mimeToExt: Record<string, string> = {
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'image/webp': 'webp',
      };
      return mimeToExt[mimeType] || 'jpg';
    };

    const fileExtension = getFileExtension(file.type);
    const fileName = `${reduxUser?.userId}-${Date.now()}.${fileExtension}`;

    const fileWithBlob = new File([filePath], fileName, {
      type: file.type,
      lastModified: Date.now(),
    });
    setSelectedFile(fileWithBlob);
    const fileUrl = URL.createObjectURL(filePath);
    setTempImageURL(fileUrl);
  };

  const submitProfileChanges = async () => {
    setOnSubmitLoader(true);
    setErrorMessage(null);
    if (!validateRequiredFields()) {
      setOnSubmitLoader(false);
      setErrorMessage(null);
      return;
    }
    let updatedProfileImageUrl = userData.profile_image_url;
    try {
      if (selectedFile && selectedFile instanceof File) {
        await initializeIdentityPool();
        const uploadResult = await uploadData({
          path: `${reduxUser?.userId}/${selectedFile.name}`,
          data: selectedFile,
        }).result;
        if (uploadResult && uploadResult.path) {
          updatedProfileImageUrl = `https://${process.env.REACT_APP_AWS_S3_BUCKET}.s3.amazonaws.com/${uploadResult.path}`;
        }
      }
    } catch (error: any) {
      console.error('upload file error', error.message);
      setOnSubmitLoader(false);
      setErrorMessage(
        error.message === 'Network Error'
          ? 'image upload failed'
          : error.message,
      );
      return;
    }

    try {
      if (reduxUser?.userId !== undefined) {
        const response = await updateProfile(
          { ...userData, profile_image_url: updatedProfileImageUrl },
          reduxUser?.userId,
        );
        if ('code' in response) {
          window.scrollTo(0, 0);
          setErrorMessage(response.message);
          setValidationErrors({});
          setIsSavedProfile(false);
        } else {
          const { family_name: lastName, given_name: firstName } =
            await updateUserAttributes({
              userAttributes: {
                given_name: userData.first_name,
                family_name: userData.last_name,
              },
            });
          if (lastName.isUpdated === true && firstName.isUpdated === true) {
            setIsSavedProfile(true);
            setValidationErrors({});
            dispatch(
              updateUserDetails({
                ...(reduxUser as IUserDetails),
                name: userData.first_name + ' ' + userData.last_name,
                schoolName: String(userData.school_id),
                departmentName: String(userData.department_id),
              }),
            );
          }
        }
        setOnSubmitLoader(false);
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setErrorMessage(error.message);
      setIsSavedProfile(false);
    }
  };

  const handleViewMyProfile = async () => {
    navigate('/profile/' + reduxUser?.userId);
  };

  return loading ? (
    <div className="loading-class">
      <Loading color="var(--black)" />
    </div>
  ) : (
    <div className="padding-container common-background custom-background grid gap-32">
      {errorMessage && <Text className="error-message">{errorMessage}</Text>}
      <Text className="display-extra-small">My Profile</Text>
      <div className="grid gap-24">
        <div className="grid gap-20">
          <Text className="label-small">
            Update your personal and professional details here.
          </Text>
          <div className="actions">
            <Button
              type="button"
              className="secondary-small-button"
              onClick={() => {
                navigate('/profile/' + reduxUser?.userId);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="primary-button-small"
              disabled={onSubmitLoader != null && onSubmitLoader}
              onClick={submitProfileChanges}
            >
              {onSubmitLoader ? (
                <>
                  <Loading size={20} /> &nbsp; Save
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
          <div className="line" />
        </div>
        <div className="grid gap-20">
          <div className="grid gap-20">
            <div className="form-row">
              <Label htmlFor="first_name" className="input-label">
                First name<sup className="fs-12">*</sup>
              </Label>
              <Input
                type="text"
                name="first_name"
                value={userData.first_name || ''}
                placeholder="Enter your First name"
                onChange={updateFormFields}
                className="input-text"
              />
              {validationErrors.first_name && (
                <div className="error-message">
                  {validationErrors.first_name}
                </div>
              )}
            </div>
            <div className="form-row">
              <Label htmlFor="last_name" className="input-label">
                Last name<sup className="fs-12">*</sup>
              </Label>
              <Input
                type="text"
                name="last_name"
                value={userData.last_name || ''}
                placeholder="Enter your Last name..."
                onChange={updateFormFields}
                className="input-text"
              />
              {validationErrors.last_name && (
                <div className="error-message">
                  {validationErrors.last_name}
                </div>
              )}
            </div>
          </div>
          <div className="line" />
          <div className="form-row">
            <Label htmlFor="email" className="input-label">
              Email address<sup className="fs-12">*</sup>
            </Label>
            <Input
              type="email"
              name="email"
              value={userData.email || ''}
              onChange={updateFormFields}
              readOnly={true}
              disabled={true}
              className="input-text"
            />
          </div>
          <div className="line" />
          <div className="grid gap-20">
            <div className="grid">
              <Label htmlFor="photo" className="field-label-bold">
                Your photo
              </Label>
              <Text className="label-small">
                This will be displayed on your profile.
              </Text>
            </div>
            <div className="grid gap-20">
              <div className="profile-preview-container">
                <img
                  src={tempImageURL !== null ? tempImageURL : NoPhoto}
                  alt={userData.first_name}
                  className="profile-preview"
                />
              </div>
              <ImageCropper onUploadComplete={handleUploadComplete} />
            </div>
          </div>
          <div className="line" />
          <div className="form-row">
            <Label htmlFor="role" className="input-label">
              University Role<sup className="fs-12">*</sup>
            </Label>
            <CustomSelect
              options={userTypes}
              onChange={(option) =>
                handleOptionChange('user_type_id', 'selected_user_type', option)
              }
              value={userData.selected_user_type ?? null}
              placeholder="Select Role"
              isMulti={false}
              isClearable={false}
              aria-describedby="userTypeError"
              isDisabled={true}
            />
            {validationErrors.user_type_id && (
              <div className="error-message">
                {validationErrors.user_type_id}
              </div>
            )}
          </div>
          <div className="line" />
          <div className="form-row">
            <Label htmlFor="opportunity" className="input-label">
              School
            </Label>
            <CustomSelect
              options={schools}
              onChange={(option) =>
                handleOptionChange('school_id', 'selected_school_id', option)
              }
              value={userData?.selected_school_id ?? null}
              placeholder="Select School"
              isMulti={false}
              isClearable={true}
              aria-describedby="userTypeError"
            />
          </div>
          <div className="form-row">
            <Label htmlFor="opportunity" className="input-label">
              Department
            </Label>
            <CustomSelect
              options={departments}
              value={userData?.selected_department_id ?? null}
              onChange={(option) =>
                handleOptionChange(
                  'department_id',
                  'selected_department_id',
                  option,
                )
              }
              placeholder="Select Department"
              isMulti={false}
              isClearable={true}
              aria-describedby="userTypeError"
            />
          </div>
          <div className="form-row">
            <Label htmlFor="opportunity" className="input-label">
              Position
            </Label>
            <Input
              type="text"
              name="headline"
              value={userData?.headline || ''} // NEED_TO_FIX
              placeholder="Enter your Position"
              onChange={updateFormFields}
              className="input-text"
            />
          </div>
          <div className="line" />
          <div className="form-row">
            <Label htmlFor="location" className="input-label">
              Location
            </Label>
            <Input
              type="text"
              name="location"
              value={userData?.location || ''}
              placeholder="Enter your Location"
              onChange={updateFormFields}
              className="input-text"
            />
            {/* <GooglePlacesAutocomplete
          apiKey="AIzaSyB88uuTbpMrqrNkfzeqZzFTDTOTbl4fKm0"
          selectProps={{
            placeholder: 'Enter your Location...',
            styles: {
              input: (provided) => ({
                ...provided,
                padding: '5px 14px',
              }),
              placeholder: (provided) => ({
                ...provided,
                color: 'var(--border-color)',
                marginLeft: 6,
              }),
            },
          }}
        />*/}
          </div>
          <div className="line" />
          <div className="form-row">
            <Label htmlFor="introduction" className="field-label-bold">
              Bio<sup className="fs-12">*</sup>
            </Label>
            <Text className="label-small">Write a short introduction.</Text>
            <Input
              type="textarea"
              name="bio"
              value={userData?.bio || ''}
              placeholder="Enter about you..."
              onChange={updateFormFields}
              className="input-text large-textarea"
            />
            <div className="field-error">
              {validationErrors.bio && (
                <div className="error-message">{validationErrors.bio}</div>
              )}
              <Text className="small-regular count-label">
                {bioCharCounts || 0}/{bioMaxChars}
              </Text>
            </div>
          </div>
          <div className="line" />
          <Label htmlFor={`supportingUrl`} className="field-label-bold">
            Professional links
          </Label>
          {reduxUser?.userTypeId === FACULTY && (
            <div className="form-row">
              <Label htmlFor={`google_scholar_url`} className="input-label">
                Google Scholar
              </Label>
              <Input
                type="text"
                name="google_scholar_url"
                value={userData.google_scholar_url}
                placeholder="https://www.example.com"
                onChange={updateFormFields}
                className="input-text small-textarea"
                readOnly={true}
                disabled={true}
              />
            </div>
          )}

          <div className="form-row">
            <Label htmlFor={`linkedin_url`} className="input-label">
              LinkedIn
            </Label>
            <Input
              type="text"
              name="linkedin_url"
              value={userData.linkedin_url}
              placeholder="https://www.example.com"
              onChange={updateFormFields}
              className="input-text small-textarea"
              readOnly={true}
              disabled={true}
            />
            {validationErrors.linkedin_url && (
              <div className="error-message">
                {validationErrors.linkedin_url}
              </div>
            )}
          </div>
          {userData.grants?.length !== 0 && <Grants userInfo={userData} />}
          <div className="actions sub-action">
            <Button
              type="button"
              className="secondary-small-button"
              onClick={() => {
                navigate('/profile/' + reduxUser?.userId);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="primary-button-small"
              disabled={onSubmitLoader != null && onSubmitLoader}
              onClick={submitProfileChanges}
            >
              {onSubmitLoader ? (
                <>
                  <Loading size={20} /> &nbsp; Save
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
          {isSavedProfile && (
            <div className="popup-overlay">
              <div className="popup-container grid gap-24">
                <div className="grid gap-16">
                  <div className="text-center">
                    <img
                      src={tempImageURL !== null ? tempImageURL : NoPhoto}
                      alt={userData.first_name}
                      className="review-photo-logo"
                    />
                  </div>
                  <div className="grid gap-8">
                    <Text className="text-bold-center">Success!</Text>
                    <Text className="success-para">
                      Your profile has been successfully updated.
                    </Text>
                  </div>
                </div>
                <Button
                  type="button"
                  className="primary-button"
                  onClick={handleViewMyProfile}
                >
                  View My Profile
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateProfile;
